import {
  AdditiveBlending,
  Box3,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Camera,
  CanvasTexture,
  DynamicDrawUsage,
  Group,
  InterleavedBufferAttribute,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Sprite,
  SpriteMaterial,
  Vector3
} from "three";
import {
  isGameAudioPageSuspended,
  playWeaponCollectSound
} from "@/lib/game-audio";

const FORK_HITBOX_NAME = "__forkHitbox";
const INFLATED_HITBOX_NAME = "__inflatedGarbageHitbox";
const PLAYER_CLEAR_RADIUS = 1.8;
const PLAYER_SOFT_CLEAR_RADIUS = 2.1;
const PLAYER_SOFT_CLEAR_SPEED = 0.08;
const PLAYER_COMFORT_MAX_RADIUS = 2.3;
const DEFAULT_FRAME_DELTA_SECONDS = 1 / 60;
const MAX_FRAME_DELTA_SECONDS = 0.05;
const FLOATING_GARBAGE_ORBIT_SPEED = 0.00005;
const GARBAGE_SPAWN_MIN_RADIUS = 1.8;
const GARBAGE_SPAWN_MAX_RADIUS = 2.3;
const SMALL_CAN_GARBAGE_SPAWN_MAX_RADIUS = 1.95;
const GARBAGE_SPAWN_MIN_SEPARATION = 0.76;
const GARBAGE_SPAWN_GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const GARBAGE_FRONT_SPAWN_BIAS = 0.58;
const GARBAGE_FRONT_SPAWN_REJECT_DOT = -0.34;
const GARBAGE_FRONT_SPAWN_REJECT_RATIO = 0.58;
const GARBAGE_FLOOR_CLEARANCE = 0.16;
const GARBAGE_FLOOR_OFFSET_KEY = "garbageFloorOffset";
const GARBAGE_VERTICAL_OFFSET_KEY = "garbageVerticalOffset";
const HEAVY_GARBAGE_VERTICAL_OFFSET = -0.7;
const GARBAGE_VERTICAL_OFFSET_BY_KEY = new Map([
  ["canister", -1.15],
]);
const GARBAGE_COLLECTION_FEEDBACK_DURATION = 520;
const HEAVY_GARBAGE_KEYS = new Set([
  "canister",
  "cannery",
  "the_tire",
]);
const SOFT_GARBAGE_KEYS = new Set([
  "chips",
  "package",
  "package2",
  "powder",
  "02_powder",
  "baloon",
  "T_short",
  "short02",
  "mask",
  "mask_02",
  "socks",
  "socks_02",
  "oil",
  "Oil",
  "black_mesh",
  "net",
  "net_02",
  "b_net",
]);
const SOFT_DEFORM_MIN_AMPLITUDE = 0.045;
const SOFT_DEFORM_MAX_AMPLITUDE = 0.22;
const SOFT_DEFORM_SIZE_RATIO = 0.17;
const SOFT_DEFORM_MESH_SCALE_PULSE = 0.1;
const SOFT_DEFORM_ROOT_SCALE_PULSE = 0.14;
const SOFT_DEFORM_ROOT_WOBBLE = 0.22;
const SOFT_DEFORM_STATE_KEY = "softGarbageDeform";
const SOFT_DEFORM_HALF_INTENSITY_KEYS = new Set(["chips"]);
const SOFT_DEFORM_POWDER_EFFECT_SCALE = 0.35;
const SOFT_DEFORM_OIL_EFFECT_SCALE = 0.85;
const SOFT_DEFORM_NET_EFFECT_SCALE = 0.7;
const SOFT_DEFORM_BALLOON_EFFECT_SCALE = 0.95;
const INFLATED_HITBOX_KEYS = new Set([
  "shampu",
  "powder",
  "02_powder",
  "big_bottle",
  "02_big_bottle",
  "bottle_02",
]);
// Общий множитель размера мусора во всех сценах. Неоновые предметы
// компенсируют это значение в GARBAGE_SCALE_BY_KEY, чтобы остаться крупнее (правка #2/#3).
const GARBAGE_GLOBAL_SCALE = 1.2;
const GARBAGE_SCALE_BY_KEY = new Map([
  // waste_water: compensate GARBAGE_GLOBAL_SCALE, make blobs visibly smaller
  ["waste_water", 0.62],
  ["fork", 1.86],
  ["cap", 1.38],
  ["chips", 1.28],
  ["glass_01", 1.18],
  ["glass_02", 1.18],
  ["02_blue_glass", 1.34],
  ["baloon", 1.26],
  ["tube", 1.16],
  ["tube_01", 1.16],
  ["oil", 1.55],
  ["Oil", 1.55],
  ["cannery", 1.65],
  ["canister", 0.56],
  ["the_tire", 0.6],
  ["02_big_bottle", 0.6],
]);
// Лёгкое самосвечение, чтобы мусор не сливался с тёмным окружением (правка #3).
const GARBAGE_EMISSIVE_INTENSITY = 0.32;
const PACKAGE_OPACITY_OVERRIDE_APPLIED_KEY = "packageOpacityOverrideApplied";
const OPAQUE_PACKAGE_KEYS = new Set(["chips", "pack", "package", "package2"]);
const PACKAGE_ALPHA_TEST = 0.08;
type PositionAttribute = BufferAttribute | InterleavedBufferAttribute;

type GarbagePlacementOptions = {
  index?: number;
  count?: number;
  occupiedPositions?: readonly Vector3[];
  minRadius?: number;
  maxRadius?: number;
  maxCameraYOffset?: number;
  garbageKey?: string;
};

type SoftGarbageMeshDeformState = {
  mesh: Mesh;
  positionAttribute: PositionAttribute;
  originalPositions: Float32Array;
  originalNormals: Float32Array | null;
  originalScale: Vector3;
  center: Vector3;
  size: Vector3;
  amplitude: number;
  phase: number;
};

type SoftGarbageDeformState = {
  meshes: SoftGarbageMeshDeformState[];
  originalScale: Vector3;
  originalRotationZ: number;
  effectScale: number;
  mode: "default" | "jelly";
  phase: number;
  lastUpdateTime: number;
};

const tmpCameraWorld = new Vector3();
const tmpCameraLocal = new Vector3();
const tmpOffset = new Vector3();
const tmpCandidatePosition = new Vector3();
const tmpSpawnOffset = new Vector3();
const tmpBestSpawnPosition = new Vector3();
const tmpSafePosition = new Vector3();
const tmpAttractDir = new Vector3();
const tmpBox = new Box3();
const tmpBoxSize = new Vector3();
const tmpBoxCenter = new Vector3();
const tmpCameraForward = new Vector3();
const tmpFeedbackWorldPosition = new Vector3();
let collectionAudioContext: AudioContext | null = null;

export function getGarbageRoot(obj: Object3D | null): Object3D | null {
  let o: Object3D | null = obj;
  while (o) {
    if (o.userData.type === "box") {
      return o;
    }
    o = o.parent;
  }
  return null;
}

function hasSkipGarbagePbr(object: Object3D): boolean {
  let current: Object3D | null = object;
  while (current) {
    if (current.userData.skipGarbagePbr === true) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

export function setupGarbageObject(object: Object3D, key: string) {
  object.userData.type = "box";
  object.userData.garbageKey = key;
  const baseKey = key.replace(/_shape_\d+$/, "");
  object.userData[GARBAGE_VERTICAL_OFFSET_KEY] = getGarbageVerticalOffset(baseKey);
  const scale = GARBAGE_SCALE_BY_KEY.get(baseKey);

  if (scale) {
    object.scale.multiplyScalar(scale);
  }

  object.scale.multiplyScalar(GARBAGE_GLOBAL_SCALE);

  if (!hasSkipGarbagePbr(object)) {
    applyGarbageVisibilityBoost(object);
  }

  applyOpaquePackageMaterial(object, baseKey);

  if (baseKey === "fork") {
    addForkHitbox(object);
  }

  if (INFLATED_HITBOX_KEYS.has(baseKey)) {
    addInflatedGarbageHitbox(object);
  }

  if (SOFT_GARBAGE_KEYS.has(baseKey)) {
    setupSoftGarbageDeformation(object, baseKey);
  }

  applyGarbageFloorClearance(object);
}

function applyGarbageVisibilityBoost(object: Object3D) {
  object.traverse((child) => {
    if (!(child instanceof Mesh) || isGarbageHitbox(child)) return;

    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];

    materials.forEach((material) => {
      if (!(material instanceof MeshStandardMaterial)) return;
      if (material.userData.garbageVisibilityBoosted === true) return;

      material.userData.garbageVisibilityBoosted = true;
      material.emissive = material.color.clone();
      material.emissiveMap = material.map;
      material.emissiveIntensity = GARBAGE_EMISSIVE_INTENSITY;
      material.needsUpdate = true;
    });
  });
}

function applyOpaquePackageMaterial(object: Object3D, key: string) {
  if (!OPAQUE_PACKAGE_KEYS.has(key)) return;

  object.traverse((child) => {
    if (!(child instanceof Mesh) || isGarbageHitbox(child)) return;

    const applyOpacityOverride = (material: MeshStandardMaterial) => {
      if (material.userData[PACKAGE_OPACITY_OVERRIDE_APPLIED_KEY] === true) {
        return material;
      }

      const nextMaterial = material.clone();
      nextMaterial.userData[PACKAGE_OPACITY_OVERRIDE_APPLIED_KEY] = true;
      nextMaterial.transparent = false;
      nextMaterial.opacity = 1;
      nextMaterial.alphaTest = Math.max(nextMaterial.alphaTest, PACKAGE_ALPHA_TEST);
      nextMaterial.depthWrite = true;
      nextMaterial.needsUpdate = true;
      return nextMaterial;
    };

    if (Array.isArray(child.material)) {
      child.material = child.material.map((material) =>
        material instanceof MeshStandardMaterial
          ? applyOpacityOverride(material)
          : material
      );
      return;
    }

    if (child.material instanceof MeshStandardMaterial) {
      child.material = applyOpacityOverride(child.material);
    }
  });
}

export function isGarbageHitbox(object: Object3D): boolean {
  return object.userData.isHitbox === true || object.name === FORK_HITBOX_NAME;
}

export function setRandomSafeGarbagePosition(
  object: Object3D,
  roomSize: number,
  scene: Object3D,
  camera: Camera,
  options: GarbagePlacementOptions = {}
) {
  const cameraLocal = getCameraPositionInSceneSpace(scene, camera);
  const count = Math.max(options.count ?? 12, 1);
  const index = options.index ?? Math.floor(Math.random() * count);
  const occupiedPositions = options.occupiedPositions ?? [];
  const minRadius = options.minRadius ?? GARBAGE_SPAWN_MIN_RADIUS;
  const requestedMaxRadius = options.maxRadius ?? GARBAGE_SPAWN_MAX_RADIUS;
  const garbageKey = options.garbageKey?.replace(/_shape_\d+$/, "");
  const maxRadius =
    garbageKey === "cannery"
      ? Math.min(requestedMaxRadius, SMALL_CAN_GARBAGE_SPAWN_MAX_RADIUS)
      : requestedMaxRadius;
  const spawnY = getGarbageSpawnY(cameraLocal, options);
  const cameraForward = getCameraForwardInSceneSpace(scene, camera);
  let hasBestSpawnPosition = false;
  let bestSpawnScore = Number.NEGATIVE_INFINITY;

  const evaluateCandidate = (candidate: Vector3) => {
    if (!isInsideRoom(candidate, roomSize)) {
      return false;
    }

    const playerDistance = getHorizontalDistance(candidate, cameraLocal);
    if (playerDistance < PLAYER_CLEAR_RADIUS) {
      return false;
    }

    const nearestDistance = getNearestDistanceToOtherGarbage(
      candidate,
      occupiedPositions
    );
    if (nearestDistance < GARBAGE_SPAWN_MIN_SEPARATION) {
      return false;
    }

    tmpOffset.subVectors(candidate, cameraLocal).setY(0);
    const candidateDistance = tmpOffset.length();
    const frontDot =
      candidateDistance > 1e-6
        ? tmpOffset.multiplyScalar(1 / candidateDistance).dot(cameraForward)
        : 0;

    if (
      frontDot < GARBAGE_FRONT_SPAWN_REJECT_DOT &&
      Math.random() < GARBAGE_FRONT_SPAWN_REJECT_RATIO
    ) {
      return false;
    }

    const roomEdgeDistance = roomSize / 2 - Math.hypot(candidate.x, candidate.z);
    const neighborScore =
      occupiedPositions.length > 0 ? nearestDistance : roomSize;
    const score =
      neighborScore +
      Math.min(playerDistance, PLAYER_COMFORT_MAX_RADIUS) * 0.18 +
      Math.max(roomEdgeDistance, 0) * 0.08 +
      Math.max(frontDot, -0.25) * GARBAGE_FRONT_SPAWN_BIAS;

    if (score > bestSpawnScore) {
      bestSpawnScore = score;
      tmpBestSpawnPosition.copy(candidate);
      hasBestSpawnPosition = true;
    }

    return true;
  };

  for (let i = 0; i < 36; i += 1) {
    const ringProgress = ((index + i * 7) % count) / count;
    const angle =
      index * GARBAGE_SPAWN_GOLDEN_ANGLE +
      i * 0.47 +
      (Math.random() - 0.5) * 0.22;
    const radius = MathUtils.lerp(
      minRadius,
      maxRadius,
      (ringProgress + Math.random() * 0.18) % 1
    );

    tmpSpawnOffset.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);

    tmpCandidatePosition.copy(cameraLocal).add(tmpSpawnOffset);
    tmpCandidatePosition.y = spawnY;

    evaluateCandidate(tmpCandidatePosition);
  }

  for (let i = 0; i < 720; i += 1) {
    const progress = i / 720;
    const ring = i % 24;
    const radius = MathUtils.lerp(
      minRadius,
      maxRadius,
      (ring + 0.5) / 24
    );
    const angle =
      (index + i * 13) * GARBAGE_SPAWN_GOLDEN_ANGLE + progress * Math.PI * 2;

    tmpCandidatePosition.set(
      cameraLocal.x + Math.cos(angle) * radius,
      spawnY,
      cameraLocal.z + Math.sin(angle) * radius
    );

    evaluateCandidate(tmpCandidatePosition);
  }

  if (hasBestSpawnPosition) {
    object.position.copy(tmpBestSpawnPosition);
    return;
  }

  object.position.copy(cameraLocal);
  object.position.y = spawnY;
  const fallbackAngle = index * GARBAGE_SPAWN_GOLDEN_ANGLE;
  placeAtPlayerClearRadius(
    object.position,
    roomSize,
    cameraLocal,
    fallbackAngle
  );
}

export function updateFloatingGarbageObject(
  object: Object3D,
  roomSize: number,
  baseCameraHeight: number,
  scene: Object3D,
  camera: Camera,
  options: {
    clampMaxYToCamera?: boolean;
    maxCameraYOffset?: number;
    enforceCameraComfortBand?: boolean;
    floatMotion?: boolean;
    attractorScenePosition?: Vector3 | null;
    attractRadius?: number;
    deltaSeconds?: number;
  } = {}
) {
  const deltaSeconds = getSafeDeltaSeconds(options.deltaSeconds);

  if (options.floatMotion !== false) {
    object.rotation.y += 0.001;
    object.rotation.x += 0.001;

    const seed = object.uuid.charCodeAt(0);
    const angle = Math.atan2(object.position.z, object.position.x);
    const orbitSpeed = (seed % 10) * FLOATING_GARBAGE_ORBIT_SPEED;

    const attractor = options.attractorScenePosition ?? null;
    const attractRadius = options.attractRadius ?? 0.9;
    let attracted = false;

    if (attractor) {
      tmpAttractDir.set(attractor.x - object.position.x, 0, attractor.z - object.position.z);
      const dist = tmpAttractDir.length();
      if (dist < attractRadius && dist > 0.001) {
        object.position.addScaledVector(
          tmpAttractDir.normalize(),
          0.022 * (1 - dist / attractRadius)
        );
        attracted = true;
      }
    }

    if (!attracted) {
      const distance = MathUtils.clamp(
        Math.sqrt(object.position.x ** 2 + object.position.z ** 2),
        1,
        roomSize / 2
      );
      const directionFactor = seed % 2 ? 1 : -1;
      object.position.x = Math.cos(angle + orbitSpeed * directionFactor) * distance;
      object.position.z = Math.sin(angle + orbitSpeed * directionFactor) * distance;
    }

    const yWaveFactor = Math.sin(angle + (attracted ? 0 : orbitSpeed) + seed) * 0.5 + 0.5;
    const yCenter = baseCameraHeight;
    object.position.y =
      yCenter -
      (yWaveFactor * baseCameraHeight) / 2 +
      getGarbageFloorOffset(object) +
      getObjectGarbageVerticalOffset(object);
    if (options.clampMaxYToCamera) {
      object.position.y = Math.min(
        object.position.y,
        getMaxCameraY(scene, camera, options.maxCameraYOffset)
      );
    }
  }

  if (options.enforceCameraComfortBand !== false) {
    moveGarbageIntoCameraComfortBand(
      object,
      roomSize,
      scene,
      camera,
      deltaSeconds
    );
  }
  updateSoftGarbageDeformation(object, performance.now() / 1000);
}

export function showGarbageCollectionFeedback(scene: Object3D, object: Object3D) {
  object.getWorldPosition(tmpFeedbackWorldPosition);
  const feedbackPosition = tmpFeedbackWorldPosition.clone();
  if (scene instanceof Group) {
    scene.worldToLocal(feedbackPosition);
  }

  const canvas = document.createElement("canvas");
  canvas.width = 192;
  canvas.height = 192;
  const context = canvas.getContext("2d");

  if (context) {
    const center = canvas.width / 2;
    const gradient = context.createRadialGradient(center, center, 4, center, center, 88);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.28, "rgba(95,255,231,0.95)");
    gradient.addColorStop(0.55, "rgba(0,197,255,0.46)");
    gradient.addColorStop(1, "rgba(0,197,255,0)");

    context.fillStyle = gradient;
    context.beginPath();
    context.arc(center, center, 88, 0, Math.PI * 2);
    context.fill();

    context.strokeStyle = "rgba(255,255,255,0.96)";
    context.lineWidth = 8;
    context.beginPath();
    context.arc(center, center, 48, 0, Math.PI * 2);
    context.stroke();

    context.strokeStyle = "rgba(130,255,245,0.88)";
    context.lineWidth = 4;
    context.beginPath();
    context.moveTo(center - 34, center);
    context.lineTo(center + 34, center);
    context.moveTo(center, center - 34);
    context.lineTo(center, center + 34);
    context.stroke();
  }

  const texture = new CanvasTexture(canvas);
  const material = new SpriteMaterial({
    map: texture,
    transparent: true,
    opacity: 1,
    depthTest: false,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const sprite = new Sprite(material);
  sprite.layers.set(1);
  sprite.renderOrder = 1_000_004;
  sprite.position.copy(feedbackPosition);
  sprite.scale.setScalar(0.28);
  scene.add(sprite);

  playWeaponCollectSound();
  playGarbageCollectionSound();

  const startedAt = performance.now();
  let frameId = 0;
  const animateFeedback = () => {
    const progress = MathUtils.clamp(
      (performance.now() - startedAt) / GARBAGE_COLLECTION_FEEDBACK_DURATION,
      0,
      1
    );
    const eased = 1 - Math.pow(1 - progress, 3);
    sprite.scale.setScalar(MathUtils.lerp(0.28, 0.96, eased));
    sprite.position.y = feedbackPosition.y + Math.sin(progress * Math.PI) * 0.1;
    material.opacity = Math.pow(1 - progress, 1.7);

    if (progress < 1) {
      frameId = requestAnimationFrame(animateFeedback);
      return;
    }

    scene.remove(sprite);
    material.dispose();
    texture.dispose();
  };

  frameId = requestAnimationFrame(animateFeedback);
  return () => {
    cancelAnimationFrame(frameId);
    scene.remove(sprite);
    material.dispose();
    texture.dispose();
  };
}

export function disposeGarbageObject(object: Object3D) {
  const deformState = object.userData[SOFT_DEFORM_STATE_KEY] as
    | SoftGarbageDeformState
    | undefined;

  deformState?.meshes.forEach(({ mesh }) => {
    mesh.geometry.dispose();
  });
  delete object.userData[SOFT_DEFORM_STATE_KEY];

  object.traverse((child) => {
    if (
      !(child instanceof Mesh) ||
      (child.name !== FORK_HITBOX_NAME && child.name !== INFLATED_HITBOX_NAME)
    ) {
      if (child instanceof Mesh) {
        disposePackageOpacityOverrideMaterial(child.material);
      }
      return;
    }

    child.geometry.dispose();
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose());
    } else {
      child.material.dispose();
    }
  });
}

function disposePackageOpacityOverrideMaterial(material: Mesh["material"]) {
  const materials = Array.isArray(material) ? material : [material];

  materials.forEach((item) => {
    if (item.userData[PACKAGE_OPACITY_OVERRIDE_APPLIED_KEY] === true) {
      item.dispose();
    }
  });
}

function setupSoftGarbageDeformation(object: Object3D, key: string) {
  const meshes: SoftGarbageMeshDeformState[] = [];
  const rootPhase = Math.random() * Math.PI * 2;
  const effectScale = getSoftGarbageEffectScale(key);

  object.traverse((child) => {
    if (!(child instanceof Mesh) || isGarbageHitbox(child)) return;

    const sourcePosition = child.geometry.getAttribute("position");
    if (!isPositionAttribute(sourcePosition)) return;

    const geometry = child.geometry.clone();
    child.geometry = geometry;

    const positionAttribute = createMutableFloatPositionAttribute(
      geometry,
      sourcePosition
    );
    const normalAttribute = geometry.getAttribute("normal");

    geometry.computeBoundingBox();
    geometry.boundingBox?.getSize(tmpBoxSize);
    geometry.boundingBox?.getCenter(tmpBoxCenter);
    const maxDimension = Math.max(tmpBoxSize.x, tmpBoxSize.y, tmpBoxSize.z, 0.001);
    const amplitude =
      MathUtils.clamp(
        maxDimension * SOFT_DEFORM_SIZE_RATIO,
        SOFT_DEFORM_MIN_AMPLITUDE,
        SOFT_DEFORM_MAX_AMPLITUDE
      ) * effectScale;

    const originalPositions = new Float32Array(positionAttribute.count * 3);
    for (let i = 0; i < positionAttribute.count; i += 1) {
      const i3 = i * 3;
      originalPositions[i3] = positionAttribute.getX(i);
      originalPositions[i3 + 1] = positionAttribute.getY(i);
      originalPositions[i3 + 2] = positionAttribute.getZ(i);
    }

    const originalNormals = isPositionAttribute(normalAttribute)
      ? new Float32Array(normalAttribute.count * 3)
      : null;
    if (originalNormals && isPositionAttribute(normalAttribute)) {
      for (let i = 0; i < normalAttribute.count; i += 1) {
        const i3 = i * 3;
        originalNormals[i3] = normalAttribute.getX(i);
        originalNormals[i3 + 1] = normalAttribute.getY(i);
        originalNormals[i3 + 2] = normalAttribute.getZ(i);
      }
    }

    meshes.push({
      mesh: child,
      positionAttribute,
      originalPositions,
      originalNormals,
      originalScale: child.scale.clone(),
      center: tmpBoxCenter.clone(),
      size: tmpBoxSize.clone(),
      amplitude,
      phase: rootPhase + meshes.length * 0.73 + Math.random() * 0.45,
    });
  });

  if (meshes.length === 0) return;

  object.userData[SOFT_DEFORM_STATE_KEY] = {
    meshes,
    originalScale: object.scale.clone(),
    originalRotationZ: object.rotation.z,
    effectScale,
    mode:
      key === "baloon" ||
        key === "T_short" ||
        key === "mask" ||
        key === "socks"
        ? "jelly"
        : "default",
    phase: rootPhase,
    lastUpdateTime: 0,
  } satisfies SoftGarbageDeformState;
}

const SOFT_DEFORM_THROTTLE_SECONDS = 1 / 30;

function updateSoftGarbageDeformation(object: Object3D, timeSeconds: number) {
  const deformState = object.userData[SOFT_DEFORM_STATE_KEY] as
    | SoftGarbageDeformState
    | undefined;
  if (!deformState) return;

  if (timeSeconds - deformState.lastUpdateTime < SOFT_DEFORM_THROTTLE_SECONDS) return;
  deformState.lastUpdateTime = timeSeconds;

  const isJelly = deformState.mode === "jelly";
  const rootT =
    timeSeconds * (isJelly ? 1.35 : 0.92) + deformState.phase;
  const rootPulse = Math.sin(rootT);
  const rootScalePulse =
    SOFT_DEFORM_ROOT_SCALE_PULSE *
    deformState.effectScale *
    (isJelly ? 1.45 : 1);
  const rootWobble =
    SOFT_DEFORM_ROOT_WOBBLE * deformState.effectScale * (isJelly ? 0.34 : 1);
  if (isJelly) {
    const squash = rootPulse * rootScalePulse;
    const delayed = Math.sin(rootT * 0.78 + 1.15) * rootScalePulse;
    object.scale.set(
      deformState.originalScale.x * (1 + squash * 0.68),
      deformState.originalScale.y * (1 - squash * 0.92),
      deformState.originalScale.z * (1 + delayed * 0.58)
    );
  } else {
    object.scale.set(
      deformState.originalScale.x * (1 + rootPulse * rootScalePulse),
      deformState.originalScale.y * (1 - rootPulse * rootScalePulse * 0.45),
      deformState.originalScale.z *
        (1 + Math.sin(rootT * 0.7 + 1.4) * rootScalePulse * 0.65)
    );
  }
  object.rotation.z =
    deformState.originalRotationZ +
    Math.sin(rootT * 1.15) * rootWobble;

  deformState.meshes.forEach((state, meshIndex) => {
    const source = state.originalPositions;
    const normals = state.originalNormals;
    const t = timeSeconds * (isJelly ? 1.18 : 0.95) + state.phase;
    const invX = state.size.x > 1e-5 ? 1 / state.size.x : 0;
    const invY = state.size.y > 1e-5 ? 1 / state.size.y : 0;
    const invZ = state.size.z > 1e-5 ? 1 / state.size.z : 0;
    const pulse = Math.sin(t * (isJelly ? 1.18 : 0.86) + meshIndex * 0.71);
    const meshScalePulse =
      SOFT_DEFORM_MESH_SCALE_PULSE *
      deformState.effectScale *
      (isJelly ? 0.72 : 1);

    state.mesh.scale.set(
      state.originalScale.x * (1 + pulse * meshScalePulse),
      state.originalScale.y * (1 - pulse * meshScalePulse * 0.55),
      state.originalScale.z * (1 + Math.sin(t * 0.68) * meshScalePulse * 0.45)
    );

    for (let i = 0; i < source.length; i += 3) {
      const x = source[i];
      const y = source[i + 1];
      const z = source[i + 2];
      const nx = (x - state.center.x) * invX;
      const ny = (y - state.center.y) * invY;
      const nz = (z - state.center.z) * invZ;
      const normalX = normals ? normals[i] : nx;
      const normalY = normals ? normals[i + 1] : ny;
      const normalZ = normals ? normals[i + 2] : nz;
      const waveA = isJelly
        ? Math.sin(t * 1.16 + ny * 3.1 + nz * 2.2)
        : Math.sin(t + ny * 7.2 + nz * 3.8);
      const waveB = isJelly
        ? Math.sin(t * 0.82 + nx * 2.8 - nz * 1.7)
        : Math.sin(t * 0.72 + nx * 6.1 - nz * 4.3);
      const waveC = isJelly
        ? Math.sin(t * 1.52 + (nx - ny) * 1.8)
        : Math.sin(t * 1.36 + (nx - ny) * 3.4);
      const localWeight =
        isJelly
          ? 0.78 +
            0.22 *
              Math.sin(
                (nx + ny + nz) * 2.8 + deformState.phase + meshIndex * 0.41
              )
          : 0.65 +
            0.35 *
              Math.sin(
                (nx + ny + nz) * 5.6 + deformState.phase + meshIndex * 0.41
              );
      const normalDisplace =
        (waveA * 0.68 + waveB * 0.22 + waveC * 0.1) *
        state.amplitude *
        localWeight *
        (isJelly ? 0.62 : 1);
      const shearDisplace =
        waveB * state.amplitude * (isJelly ? 0.18 : 0.35) * localWeight;

      state.positionAttribute.setXYZ(
        i / 3,
        x + normalX * normalDisplace + shearDisplace * 0.55,
        y + normalY * normalDisplace + waveC * state.amplitude * 0.28,
        z + normalZ * normalDisplace + shearDisplace * 0.85
      );
    }

    state.positionAttribute.needsUpdate = true;
  });
}

function getSoftGarbageEffectScale(key: string) {
  if (key === "baloon") {
    return SOFT_DEFORM_BALLOON_EFFECT_SCALE;
  }

  if (key === "powder" || key === "02_powder") {
    return SOFT_DEFORM_POWDER_EFFECT_SCALE;
  }

  if (key === "oil" || key === "black_mesh") {
    return SOFT_DEFORM_OIL_EFFECT_SCALE;
  }

  if (key === "net" || key === "net_02" || key === "b_net") {
    return SOFT_DEFORM_NET_EFFECT_SCALE;
  }

  return SOFT_DEFORM_HALF_INTENSITY_KEYS.has(key) ? 0.5 : 1;
}

function isPositionAttribute(
  attribute: unknown
): attribute is PositionAttribute {
  return (
    attribute instanceof BufferAttribute ||
    attribute instanceof InterleavedBufferAttribute
  );
}

function createMutableFloatPositionAttribute(
  geometry: BufferGeometry,
  sourcePosition: PositionAttribute
) {
  if (
    sourcePosition instanceof BufferAttribute &&
    sourcePosition.array instanceof Float32Array &&
    !sourcePosition.normalized
  ) {
    sourcePosition.setUsage(DynamicDrawUsage);
    return sourcePosition;
  }

  const positions = new Float32Array(sourcePosition.count * 3);
  for (let index = 0; index < sourcePosition.count; index += 1) {
    const i3 = index * 3;
    positions[i3] = sourcePosition.getX(index);
    positions[i3 + 1] = sourcePosition.getY(index);
    positions[i3 + 2] = sourcePosition.getZ(index);
  }

  const positionAttribute = new BufferAttribute(positions, 3);
  positionAttribute.setUsage(DynamicDrawUsage);
  geometry.setAttribute("position", positionAttribute);

  return positionAttribute;
}

function addForkHitbox(object: Object3D) {
  if (object.getObjectByName(FORK_HITBOX_NAME)) return;

  object.updateMatrixWorld(true);
  tmpBox.setFromObject(object);
  tmpBox.getSize(tmpBoxSize);
  tmpBox.getCenter(tmpBoxCenter);

  const width = Math.max(tmpBoxSize.x * 1.8, 0.45);
  const height = Math.max(tmpBoxSize.y * 1.8, 0.45);
  const depth = Math.max(tmpBoxSize.z * 1.8, 0.45);

  const hitbox = new Mesh(
    new BoxGeometry(width, height, depth),
    new MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      colorWrite: false
    })
  );

  hitbox.name = FORK_HITBOX_NAME;
  hitbox.position.copy(tmpBoxCenter);
  hitbox.userData.isHitbox = true;
  hitbox.visible = false;
  hitbox.renderOrder = -1;
  object.add(hitbox);
}

function addInflatedGarbageHitbox(object: Object3D) {
  if (object.getObjectByName(INFLATED_HITBOX_NAME)) return;

  object.updateMatrixWorld(true);
  tmpBox.setFromObject(object);
  tmpBox.getSize(tmpBoxSize);
  tmpBox.getCenter(tmpBoxCenter);
  object.worldToLocal(tmpBoxCenter);

  const width = Math.max(tmpBoxSize.x * 1.65, 0.62);
  const height = Math.max(tmpBoxSize.y * 1.55, 0.62);
  const depth = Math.max(tmpBoxSize.z * 1.65, 0.62);

  const hitbox = new Mesh(
    new BoxGeometry(width, height, depth),
    new MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      colorWrite: false
    })
  );

  hitbox.name = INFLATED_HITBOX_NAME;
  hitbox.position.copy(tmpBoxCenter);
  hitbox.userData.isHitbox = true;
  hitbox.visible = false;
  hitbox.renderOrder = -1;
  object.add(hitbox);
}

function applyGarbageFloorClearance(object: Object3D) {
  object.updateWorldMatrix(true, true);
  tmpBox.setFromObject(object);
  if (tmpBox.isEmpty()) return;

  const offset = Math.max(0, object.position.y + GARBAGE_FLOOR_CLEARANCE - tmpBox.min.y);
  object.userData[GARBAGE_FLOOR_OFFSET_KEY] = offset;
  object.position.y += offset;
}

function getGarbageFloorOffset(object: Object3D) {
  const offset = object.userData[GARBAGE_FLOOR_OFFSET_KEY];
  return typeof offset === "number" && Number.isFinite(offset) ? offset : 0;
}

export function getGarbageVerticalOffset(key: string) {
  const baseKey = key.replace(/_shape_\d+$/, "");
  const offset = GARBAGE_VERTICAL_OFFSET_BY_KEY.get(baseKey);
  if (offset !== undefined) return offset;

  return HEAVY_GARBAGE_KEYS.has(baseKey) ? HEAVY_GARBAGE_VERTICAL_OFFSET : 0;
}

function getObjectGarbageVerticalOffset(object: Object3D) {
  const offset = object.userData[GARBAGE_VERTICAL_OFFSET_KEY];
  return typeof offset === "number" && Number.isFinite(offset) ? offset : 0;
}

export function keepAwayFromCamera(
  object: Object3D,
  roomSize: number,
  scene: Object3D,
  camera: Camera
) {
  const cameraLocal = getCameraPositionInSceneSpace(scene, camera);
  moveGarbageOutOfUnsafeCameraZone(object, roomSize, scene, camera, cameraLocal);
}

function isGarbageUnsafeForCamera(
  object: Object3D,
  cameraLocal: Vector3
) {
  const distance = getHorizontalDistance(object.position, cameraLocal);
  return distance < PLAYER_CLEAR_RADIUS;
}

function moveGarbageOutOfUnsafeCameraZone(
  object: Object3D,
  roomSize: number,
  scene: Object3D,
  camera: Camera,
  cameraLocal = getCameraPositionInSceneSpace(scene, camera)
) {
  if (!isGarbageUnsafeForCamera(object, cameraLocal)) {
    return;
  }

  tmpOffset
    .subVectors(object.position, cameraLocal)
    .setY(0);

  if (tmpOffset.lengthSq() < 1e-6) {
    const fallbackAngle = getStableObjectAngle(object);
    tmpOffset.set(Math.cos(fallbackAngle), 0, Math.sin(fallbackAngle));
  }

  tmpSafePosition.copy(object.position);
  placeAtPlayerClearRadius(
    tmpSafePosition,
    roomSize,
    cameraLocal,
    Math.atan2(tmpOffset.z, tmpOffset.x)
  );
  object.position.copy(tmpSafePosition);
}

function moveGarbageIntoCameraComfortBand(
  object: Object3D,
  roomSize: number,
  scene: Object3D,
  camera: Camera,
  deltaSeconds: number
) {
  const cameraLocal = getCameraPositionInSceneSpace(scene, camera);

  if (isGarbageUnsafeForCamera(object, cameraLocal)) {
    moveGarbageOutOfUnsafeCameraZone(
      object,
      roomSize,
      scene,
      camera,
      cameraLocal
    );
    return;
  }

  tmpOffset
    .subVectors(object.position, cameraLocal)
    .setY(0);

  const distance = tmpOffset.length();

  if (distance < 1e-6) {
    return;
  }

  tmpOffset.normalize();
  tmpSafePosition.copy(object.position);

  if (distance < PLAYER_SOFT_CLEAR_RADIUS) {
    tmpSafePosition.x = cameraLocal.x + tmpOffset.x * PLAYER_SOFT_CLEAR_RADIUS;
    tmpSafePosition.z = cameraLocal.z + tmpOffset.z * PLAYER_SOFT_CLEAR_RADIUS;
    clampToRoom(tmpSafePosition, roomSize);
    object.position.lerp(
      tmpSafePosition,
      getDeltaScaledAlpha(PLAYER_SOFT_CLEAR_SPEED, deltaSeconds)
    );
    return;
  }

  if (distance <= PLAYER_COMFORT_MAX_RADIUS) {
    return;
  }

  tmpSafePosition.x = cameraLocal.x + tmpOffset.x * PLAYER_COMFORT_MAX_RADIUS;
  tmpSafePosition.z = cameraLocal.z + tmpOffset.z * PLAYER_COMFORT_MAX_RADIUS;
  clampToRoom(tmpSafePosition, roomSize);
  object.position.lerp(
    tmpSafePosition,
    getDeltaScaledAlpha(PLAYER_SOFT_CLEAR_SPEED, deltaSeconds)
  );
}

function getNearestDistanceToOtherGarbage(
  position: Vector3,
  occupiedPositions: readonly Vector3[]
) {
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const occupiedPosition of occupiedPositions) {
    nearestDistance = Math.min(
      nearestDistance,
      getHorizontalDistance(position, occupiedPosition)
    );
  }

  return nearestDistance;
}

function placeAtPlayerClearRadius(
  target: Vector3,
  roomSize: number,
  cameraLocal: Vector3,
  baseAngle: number
) {
  const y = target.y;
  const angleStep = (Math.PI * 2) / 96;

  for (let i = 0; i < 96; i += 1) {
    const step = Math.ceil(i / 2);
    const direction = i % 2 === 0 ? -1 : 1;
    const angle = baseAngle + direction * step * angleStep;

    tmpCandidatePosition.set(
      cameraLocal.x + Math.cos(angle) * PLAYER_CLEAR_RADIUS,
      y,
      cameraLocal.z + Math.sin(angle) * PLAYER_CLEAR_RADIUS
    );

    if (isInsideRoom(tmpCandidatePosition, roomSize)) {
      target.copy(tmpCandidatePosition);
      return;
    }
  }

  const roomRadius = roomSize / 2;
  tmpOffset.set(cameraLocal.x, 0, cameraLocal.z);
  if (tmpOffset.lengthSq() < 1e-6) {
    target.set(
      Math.cos(baseAngle) * roomRadius,
      y,
      Math.sin(baseAngle) * roomRadius
    );
    return;
  }

  tmpOffset.normalize();
  target.set(-tmpOffset.x * roomRadius, y, -tmpOffset.z * roomRadius);
}

function getSafeDeltaSeconds(deltaSeconds: number | undefined) {
  if (deltaSeconds === undefined || !Number.isFinite(deltaSeconds)) {
    return DEFAULT_FRAME_DELTA_SECONDS;
  }

  return MathUtils.clamp(deltaSeconds, 0, MAX_FRAME_DELTA_SECONDS);
}

function getDeltaScaledAlpha(alphaPerFrame: number, deltaSeconds: number) {
  const frameScale = deltaSeconds / DEFAULT_FRAME_DELTA_SECONDS;
  return 1 - Math.pow(1 - alphaPerFrame, frameScale);
}

function getCameraPositionInSceneSpace(scene: Object3D, camera: Camera) {
  camera.getWorldPosition(tmpCameraWorld);
  tmpCameraLocal.copy(tmpCameraWorld);

  if (scene instanceof Group) {
    scene.worldToLocal(tmpCameraLocal);
  }

  return tmpCameraLocal;
}

function getCameraForwardInSceneSpace(scene: Object3D, camera: Camera) {
  camera.getWorldDirection(tmpCameraForward);

  if (scene instanceof Group) {
    scene.worldToLocal(tmpCameraForward.add(tmpCameraWorld.set(0, 0, 0)));
    scene.worldToLocal(tmpCameraWorld.set(0, 0, 0));
    tmpCameraForward.sub(tmpCameraWorld);
  }

  tmpCameraForward.setY(0);
  if (tmpCameraForward.lengthSq() < 1e-6) {
    tmpCameraForward.set(0, 0, -1);
  } else {
    tmpCameraForward.normalize();
  }

  return tmpCameraForward;
}

function playGarbageCollectionSound() {
  if (typeof window === "undefined") return;
  if (isGameAudioPageSuspended()) return;

  const AudioContextCtor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) return;

  collectionAudioContext ??= new AudioContextCtor();
  const audioContext = collectionAudioContext;

  if (audioContext.state === "suspended") {
    void audioContext.resume();
  }

  const now = audioContext.currentTime;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  const filter = audioContext.createBiquadFilter();

  oscillator.type = "triangle";
  oscillator.frequency.setValueAtTime(520, now);
  oscillator.frequency.exponentialRampToValueAtTime(980, now + 0.055);
  oscillator.frequency.exponentialRampToValueAtTime(420, now + 0.14);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(1800, now);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.08, now + 0.018);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

  oscillator.connect(filter);
  filter.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.17);
}

function getGarbageSpawnY(
  cameraLocal: Vector3,
  options: GarbagePlacementOptions
) {
  const baseY = getGarbageVerticalOffset(options.garbageKey ?? "");
  if (options.maxCameraYOffset === undefined) {
    return baseY;
  }

  return Math.min(baseY, cameraLocal.y - options.maxCameraYOffset);
}

function getMaxCameraY(
  scene: Object3D,
  camera: Camera,
  maxCameraYOffset = 0
) {
  return getCameraPositionInSceneSpace(scene, camera).y - maxCameraYOffset;
}

function getHorizontalDistance(a: Vector3, b: Vector3) {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

function isInsideRoom(position: Vector3, roomSize: number) {
  return Math.hypot(position.x, position.z) <= roomSize / 2;
}

function getStableObjectAngle(object: Object3D) {
  let hash = 0;
  for (let i = 0; i < object.uuid.length; i += 1) {
    hash = (hash * 31 + object.uuid.charCodeAt(i)) | 0;
  }

  return MathUtils.euclideanModulo(hash, 360) * MathUtils.DEG2RAD;
}

function clampToRoom(position: Vector3, roomSize: number) {
  const radius = roomSize / 2;
  const distance = Math.sqrt(position.x ** 2 + position.z ** 2);
  if (distance <= radius || distance < 1e-6) return;

  position.x = (position.x / distance) * radius;
  position.z = (position.z / distance) * radius;
}
